<template>
  <div class="appointmentsContainer p-4 my-4">
    <h2>{{ $t('appointmentPage.appointments.selectedAppointments') }}</h2>
    <div v-if="appointments.length > 0">
      <DataTable
        id="appointments-table"
        class="no-header custom-striped"
        :value="appointments"
        striped-rows
        responsive-layout="stack"
        breakpoint="540px"
      >
        <Column
          v-if="appointmentOnOffice"
          body-class="font-semibold"
          :header="$t('entity.appointment.office')"
        >
          <template #body="{data}">
            {{ data.office.name }}
          </template>
        </Column>
        <Column
          v-else
          body-class="font-semibold"
          :header="$t('entity.appointment.office')"
        >
          <template #body="{data}">
            {{ data.roamingTeam.name }}
          </template>
        </Column>
        <Column
          body-class="font-semibold"
          :header="$t('entity.appointment.day')"
        >
          <template #body="{data}">
            {{ getAppointmentDay(data.start) }}
          </template>
        </Column>
        <Column
          body-class="font-semibold"
          :header="$t('entity.appointment.appointmentTime')"
        >
          <template #body="{data}">
            {{ getAppointmentStartTime(data.start) }} - {{ getAppointmentEndTime(data.end) }}
          </template>
        </Column>
        <Column :header="$t('entity.appointment.duration')">
          <template #body="{data}">
            <span>({{ $t('entity.appointment.duration') }} : {{ getAppointmentDurationString(data) }})</span>
          </template>
        </Column>
        <Column
          body-class="text-center"
          :header="$t('appointmentPage.appointments.removeAppointment')"
        >
          <template #body="{data}">
            <Button
              icon="pi pi-times"
              :title="$t('appointmentPage.appointments.removeAppointment')"
              class="p-button-rounded p-button-secondary"
              @click="removeAppointment(data)"
            />
          </template>
        </Column>
      </DataTable>

      <div class="text-right mt-4">
        <p v-if="remainingDurationToScheduleString > 0">
          <i class="pi pi-clock" /> {{ $t('appointmentPage.appointments.remainingTime') }}
          <span class="remaining-duration">{{ remainingDurationToScheduleString }}</span>
        </p>
        <Button
          type="submit"
          class="p-button-rounded p-button-outlined"
          :label="$t('appointmentPage.appointments.validateAppointments')"
          :disabled="appointmentValidationDisable"
          @click="bookAppointment"
        />
        <Message
          v-if="errorAppointmentExceedsControlDuration"
          severity="error"
          :closable="false"
        >
          {{ $t('validations.appointment.exceedsControlDuration') }}
        </Message>
      </div>
    </div>
    <div v-else>
      <p>{{ $t('appointmentPage.appointments.noSelectedAppointment') }}</p>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {
  convertDateStringToDayLocaleString,
  convertDateStringToTimeLocaleString,
  convertMinsToHrsMinsString
} from "@/shared/utils/dateTimeUtils";
import {getAppointmentDuration, getTotalAppointmentsDuration} from "@/shared/utils/appointmentSlotUtils";
import Message from "primevue/message";
import moment from "moment";
import RequestCreateRequest from "@/models/dto/requests/RequestCreateRequest";

export default {
  name: 'RequestAppointments',
  components: {Button, DataTable, Column, Message},
  props: {
    appointments: {
      type: Array,
      required: true
    },
    controlDuration: {
      type: Number,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    equipmentsNumber: {
      type: Number,
      default: 0
    },
    appointmentOnOffice: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:appointments', 'booked'],
  data() {
    return {
      errorAppointmentExceedsControlDuration: false,
    }
  },
  computed: {
    appointmentsModel: {
      get() {
        return this.appointments;
      },
      set(value) {
        this.$emit('update:appointments', value)
      }
    },
    appointmentValidationDisable() {
      return this.getTotalAppointmentsDuration() !== this.controlDuration
    },
    remainingDurationToScheduleString() {
      let remainingMinutes = this.remainingMinToSchedule();
      return convertMinsToHrsMinsString(remainingMinutes)
    }
  },

  methods: {
    remainingMinToSchedule() {
      let remainingMinToSchedule = this.controlDuration - this.getTotalAppointmentsDuration()
      this.errorAppointmentExceedsControlDuration = remainingMinToSchedule < 0
      return remainingMinToSchedule
    },
    removeAppointment(appointmentToRemove) {
      this.appointmentsModel = this.appointments.filter(appointment => appointment !== appointmentToRemove)
    },
    getAppointmentDay(dateString) {
      return convertDateStringToDayLocaleString(dateString, this.$i18n.locale)
    },
    getAppointmentStartTime(dateString) {
      return convertDateStringToTimeLocaleString(dateString, this.$i18n.locale)
    },
    getAppointmentEndTime(dateString) {
      return convertDateStringToTimeLocaleString(dateString, this.$i18n.locale)
    },
    getTotalAppointmentsDuration() {
      return getTotalAppointmentsDuration(this.appointments);
    },
    getAppointmentDuration(appointment) {
      return getAppointmentDuration(appointment)
    },
    getAppointmentDurationString(appointment) {
      let duration = this.getAppointmentDuration(appointment)
      return convertMinsToHrsMinsString(duration)
    },
    async bookAppointment() {
      const requestData = new RequestCreateRequest({
        userId: this.user.id,
        appointments: [],
        nbEquipments: this.equipmentsNumber,
        specialRequest: true
      })

      for (const appointment of this.appointments) {
        requestData.appointments.push({
          start: moment(appointment.start).format('YYYY-MM-DDTHH:mm'),
          end: moment(appointment.end).format('HH:mm'),
          officeId: appointment.office?.id,
          roamingTeamId: appointment.roamingTeam?.id
        })
      }
      try {
        const request = await this.$requestService.createRequest(requestData)
        this.$emit('booked', request)
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit('toast', {severity: 'error', message: errors})
        this.$emitter.emit('book-failed')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.appointmentsContainer {
  background-color: $light-grey;
}

.p-button.p-button-secondary.p-button-icon-only {
  background-color: white;
  color: $dark-grey;
  width: 2rem;
  height: 2rem;
}

::v-deep(.no-header.p-datatable) {
  thead {
    display: none;
  }
}

.remaining-duration {
  color: $pink;
  font-weight: 600;
}

::v-deep(.no-header.p-datatable) {
  thead {
    display: none;
  }
}
</style>
